// src/screens/PlanDetails.tsx
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  NotebookPen,
  Timer,
  Layers3,
  CalendarDays,
  Users,
} from "lucide-react";
import { TrainingDrawingCanvas } from "../components/TrainingDrawingCanvas";
import i18n from "../i18n";

type BlockKind = "echauffement" | "exercice" | "prepa";

type ExerciseBlock = {
  id: string;
  kind: BlockKind;
  name: string;
  title?: string;
  duration: string;
  objectives: string[];
  description: string;
  technicalNotes: string;
  evolutions: string;
  drawings: {
    id: string;
    paths: { x: number; y: number }[][];
    type: "half" | "full";
  }[];
};

type TrainingPlanRow = { id: string; training_id: string; exercises: ExerciseBlock[] };
type TrainingRow = { id: string; date: string; team_id: string | null };
type TeamRow = { id: string; team_name: string };

type TemplateRow = {
  id: string;
  name: string;
  kind: BlockKind;
  payload: ExerciseBlock;
  created_at: string;
};

/* ===== i18n mini-hook ===== */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  // stable par langue : sinon `t` change à chaque rendu et relance les useEffect (boucle de chargement)
  const t = useCallback(
    (key: string, options?: Record<string, unknown>): string =>
      options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lng]
  );
  return { t, lng };
}

export const PlanDetails = () => {
  const { t, lng } = useI18nSafe();
  const nav = useNavigate();
  const [params] = useSearchParams();

  const templateId = params.get("template_id");
  const planId = params.get("plan_id");
  const trainingId = params.get("training_id");

  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState<string>(t("plandetails:default_title_preview"));
  const [exercises, setExercises] = useState<ExerciseBlock[]>([]);
  const [trainingMeta, setTrainingMeta] = useState<{ date?: string; teamName?: string }>({});

  // labels de type de bloc (dépendants de la langue)
  const KIND_LABEL: Record<BlockKind, string> = useMemo(
    () => ({
      echauffement: t("plandetails:kinds.warmup"),
      exercice: t("plandetails:kinds.exercise"),
      prepa: t("plandetails:kinds.strength"),
    }),
    [t]
  );

  useEffect(() => {
    (async () => {
      setLoading(true);

      // 1) Aperçu d'un modèle
      if (templateId) {
        const { data, error } = await supabase
          .from("training_block_templates")
          .select("id,name,kind,payload,created_at")
          .eq("id", templateId)
          .maybeSingle<TemplateRow>();

        if (!error && data?.payload) {
          setExercises([data.payload]);
          setTitle(t("plandetails:title_template", { name: data.name }));
        }
        setLoading(false);
        return;
      }

      // 2) Aperçu d'un plan par plan_id
      if (planId) {
        const { data, error } = await supabase
          .from("training_plans")
          .select("id,training_id,exercises")
          .eq("id", planId)
          .maybeSingle<TrainingPlanRow>();

        if (!error && data) {
          setExercises((data.exercises || []) as ExerciseBlock[]);
          await hydrateTrainingHeader(data.training_id);
        }
        setLoading(false);
        return;
      }

      // 3) Aperçu d'un plan par training_id
      if (trainingId) {
        const { data, error } = await supabase
          .from("training_plans")
          .select("id,training_id,exercises")
          .eq("training_id", trainingId)
          .maybeSingle<TrainingPlanRow>();

        if (!error && data) {
          setExercises((data.exercises || []) as ExerciseBlock[]);
          await hydrateTrainingHeader(data.training_id);
        } else {
          setExercises([]); // pas de plan
          await hydrateTrainingHeader(trainingId);
        }
        setLoading(false);
        return;
      }

      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, planId, trainingId, t, lng]);

  const hydrateTrainingHeader = async (tid?: string) => {
    if (!tid) {
      setTitle(t("plandetails:default_title_preview"));
      setTrainingMeta({});
      return;
    }
    const { data: tr } = await supabase
      .from("trainings")
      .select("id,date,team_id")
      .eq("id", tid)
      .maybeSingle<TrainingRow>();

    let teamName: string | undefined;
    if (tr?.team_id) {
      const { data: team } = await supabase
        .from("teams")
        .select("id,team_name")
        .eq("id", tr.team_id)
        .maybeSingle<TeamRow>();
      teamName = team?.team_name;
    }

    const locale = lng === "fr" ? "fr-FR" : "en-US";
    const dateLabel = tr?.date
      ? new Date(tr.date).toLocaleString(locale, {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : undefined;

    setTitle(t("plandetails:default_title_training"));
    setTrainingMeta({ date: dateLabel, teamName });
  };

  const hasContent = useMemo(() => exercises && exercises.length > 0, [exercises]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      <div className="max-w-5xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => nav(-1)}
            className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400"
            aria-label={t("common:back")}
          >
            <ArrowLeft className="w-4 h-4" />
            {t("common:back")}
          </button>
        </div>

        <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
          <NotebookPen className="w-6 h-6" />
          {title}
        </h1>

        {(trainingMeta.date || trainingMeta.teamName) && (
          <div className="mb-5 flex flex-wrap items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
            {trainingMeta.date && (
              <span className="inline-flex items-center gap-1">
                <CalendarDays className="w-4 h-4" />
                {trainingMeta.date}
              </span>
            )}
            {trainingMeta.teamName && (
              <span className="inline-flex items-center gap-1">
                <Users className="w-4 h-4" />
                {trainingMeta.teamName}
              </span>
            )}
          </div>
        )}

        {loading ? (
          <p className="text-gray-600 dark:text-gray-300">{t("common:loading")}</p>
        ) : !hasContent ? (
          <div className="rounded-2xl border bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 p-6">
            <p className="text-gray-700 dark:text-gray-300">
              {t("plandetails:empty.no_content")}
              {trainingId ? (
                <>
                  {" "}{t("plandetails:empty.no_plan_for_training")}{" "}
                  <Link
                    to={`/plantraining?training_id=${trainingId}`}
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {t("plandetails:empty.create_or_edit")}
                  </Link>
                  .
                </>
              ) : null}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {exercises.map((ex, idx) => {
              const labelKind = KIND_LABEL[ex.kind] ?? t("plandetails:kinds.block");
              const headerLine = [labelKind, ex.name].filter(Boolean).join(" · ");

              return (
                <div
                  key={ex.id || idx}
                  className="rounded-2xl border bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 p-4 shadow-sm"
                >
                  {/* En-tête bloc */}
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                    <h2 className="font-semibold text-lg">{headerLine}</h2>
                    <div className="flex items-center gap-2 text-xs">
                      {ex.duration ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border
                                        border-emerald-300 bg-emerald-50 text-emerald-700
                                        dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-200">
                          <Timer className="w-3 h-3" />
                          {ex.duration}
                        </span>
                      ) : null}
                      <span className="inline-flex items-center gap-1 text-gray-500 dark:text-gray-400">
                        <Layers3 className="w-3 h-3" />
                        {t("plandetails:drawings.count", { count: ex.drawings?.length ?? 0 })}
                      </span>
                    </div>
                  </div>

                  {/* Objectifs */}
                  {ex.objectives?.length ? (
                    <div className="mb-3">
                      <div className="text-sm font-medium mb-1">{t("plandetails:sections.objectives")}</div>
                      <ul className="list-disc pl-5 text-sm text-gray-800 dark:text-gray-200 space-y-0.5">
                        {ex.objectives.filter(Boolean).map((o, i) => (
                          <li key={i}>{o}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {/* Texte */}
                  {ex.description ? (
                    <div className="mb-3">
                      <div className="text-sm font-medium mb-1">{t("plandetails:sections.procedure")}</div>
                      <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                        {ex.description}
                      </p>
                    </div>
                  ) : null}

                  {ex.technicalNotes ? (
                    <div className="mb-3">
                      <div className="text-sm font-medium mb-1">{t("plandetails:sections.tech_notes")}</div>
                      <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                        {ex.technicalNotes}
                      </p>
                    </div>
                  ) : null}

                  {ex.evolutions ? (
                    <div className="mb-3">
                      <div className="text-sm font-medium mb-1">{t("plandetails:sections.evolutions")}</div>
                      <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                        {ex.evolutions}
                      </p>
                    </div>
                  ) : null}

                  {/* Dessins */}
                  {ex.drawings?.length ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {ex.drawings.map((d) => (
                        <div key={d.id} className="border rounded-lg dark:border-gray-700 p-2">
                          <TrainingDrawingCanvas
                            initialPaths={d.paths}
                            type={d.type}
                            readOnly
                            targetWidth={320}
                            onSave={() => {}}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {t("plandetails:drawings.none")}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
